import { useState, useEffect } from 'react';
import {
  EPOXY_PRIMERS,
  DESIGN_COAT_PRODUCTS,
  PIGMENTS_PRODUCTS,
  TOP_COAT_PRODUCTS,
  FLOOR_PREP_PRODUCTS
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';

const useMetallicOrder = (currentStep) => {
  // Legacy state
  const [needsSmoothingCompounds, setNeedsSmoothingCompounds] = useState(null);
  const [needsPrimer, setNeedsPrimer] = useState(null);
  const [primerAreas, setPrimerAreas] = useState([]);
  const [designCoatSelections, setDesignCoatSelections] = useState({});
  const [topCoatSelections, setTopCoatSelections] = useState({ productType: null, finish: null, packs: [] });

  // Areas (new system)
  const [areas, setAreas] = useState([]);

  const generateAreaId = () => `area_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // New area system functions
  const addArea = () => {
    setAreas(prev => [...prev, {
      id: generateAreaId(),
      name: `Area ${prev.length + 1}`,
      m2: '',
      needsSmoothingCompounds: null,
      smoothingCompounds: {},
      needsSubFloorPrimer: null,
      subFloorPrimers: {},
      needsPrimer: null,
      primerType: '1_part',
      primerColor: 'White',
      primerPacks: [],
      designCoatType: 'comfort',
      designCoatPacks: [],
      crystalUnits: 0,
      topCoatType: null,
      topCoatFinish: null,
      topCoatPacks: []
    }]);
  };

  const removeArea = (areaId) => {
    setAreas(prev => prev.filter(a => a.id !== areaId));
  };

  const updateArea = (areaId, field, value) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      return { ...area, [field]: value };
    }));
  };

  const updateAreaWithPrimerCalc = (areaId, field, value) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const updated = { ...area, [field]: value };
      if (field === 'm2' || field === 'primerType') {
        const m2Value = field === 'm2' ? parseInt(value) || 0 : parseInt(area.m2) || 0;
        const type = field === 'primerType' ? value : area.primerType;
        if (m2Value > 0 && type && area.needsPrimer) {
          updated.primerPacks = calculateOptimalPacks(m2Value, EPOXY_PRIMERS[type].packs);
        }
      }
      if (field === 'primerType') {
        const availableColors = EPOXY_PRIMERS[value].colors;
        if (!availableColors.includes(area.primerColor)) {
          updated.primerColor = availableColors[0];
        }
      }
      return updated;
    }));
  };

  const initializePrimerPacks = (areaId) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const m2Value = parseInt(area.m2) || 0;
      if (m2Value > 0 && area.needsPrimer) {
        return { ...area, primerPacks: calculateOptimalPacks(m2Value, EPOXY_PRIMERS[area.primerType].packs) };
      }
      return area;
    }));
  };

  const updateAreaPrimerPackQty = (areaId, packSize, delta) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const existingPack = area.primerPacks.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = area.primerPacks.filter(p => p.size !== packSize); }
        else { newPacks = area.primerPacks.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = EPOXY_PRIMERS[area.primerType].packs.find(p => p.size === packSize);
        newPacks = [...area.primerPacks, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = area.primerPacks; }
      return { ...area, primerPacks: newPacks };
    }));
  };

  const initializeDesignCoat = (areaId) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const m2Value = parseInt(area.m2) || 0;
      if (m2Value > 0) {
        return {
          ...area,
          designCoatPacks: calculateOptimalPacks(m2Value, DESIGN_COAT_PRODUCTS.comfort.packs),
          crystalUnits: Math.ceil(m2Value / DESIGN_COAT_PRODUCTS.crystal.coverageMin)
        };
      }
      return area;
    }));
  };

  const updateDesignCoatType = (areaId, productType) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const m2Value = parseInt(area.m2) || 0;
      return {
        ...area,
        designCoatType: productType,
        designCoatPacks: productType === 'comfort' ? calculateOptimalPacks(m2Value, DESIGN_COAT_PRODUCTS.comfort.packs) : [],
        crystalUnits: productType === 'crystal' ? Math.ceil(m2Value / DESIGN_COAT_PRODUCTS.crystal.coverageMin) : 0
      };
    }));
  };

  const updateDesignCoatPackQty = (areaId, packSize, delta) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const existingPack = area.designCoatPacks.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = area.designCoatPacks.filter(p => p.size !== packSize); }
        else { newPacks = area.designCoatPacks.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = DESIGN_COAT_PRODUCTS.comfort.packs.find(p => p.size === packSize);
        newPacks = [...area.designCoatPacks, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = area.designCoatPacks; }
      return { ...area, designCoatPacks: newPacks };
    }));
  };

  const updateCrystalUnits = (areaId, delta) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      return { ...area, crystalUnits: Math.max(0, area.crystalUnits + delta) };
    }));
  };

  const initializeTopCoat = (areaId, productType) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const m2Value = parseInt(area.m2) || 0;
      const product = TOP_COAT_PRODUCTS[productType];
      return {
        ...area,
        topCoatType: productType,
        topCoatFinish: product.finishes[0],
        topCoatPacks: calculateOptimalPacks(m2Value, product.packs)
      };
    }));
  };

  const updateTopCoatFinish = (areaId, finish) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      return { ...area, topCoatFinish: finish };
    }));
  };

  const updateTopCoatPackQty = (areaId, packSize, delta) => {
    setAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const product = TOP_COAT_PRODUCTS[area.topCoatType];
      const existingPack = area.topCoatPacks.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = area.topCoatPacks.filter(p => p.size !== packSize); }
        else { newPacks = area.topCoatPacks.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = product.packs.find(p => p.size === packSize);
        newPacks = [...area.topCoatPacks, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = area.topCoatPacks; }
      return { ...area, topCoatPacks: newPacks };
    }));
  };

  // Pigments state
  const [pigmentSelections, setPigmentSelections] = useState({
    metallicPowders: [],
    pigmentPaste: [],
    glitter: [],
    marbleSprays: [],
    metallicSprays: []
  });

  // Auto-initialize primer packs when entering Step 3
  useEffect(() => {
    if (currentStep === 3 && primerAreas.length > 0) {
      setPrimerAreas(prev => prev.map(area => {
        const m2Value = parseInt(area.m2) || 0;
        if (m2Value > 0 && area.packs.length === 0) {
          return { ...area, needsPrimer: true, packs: calculateOptimalPacks(m2Value, EPOXY_PRIMERS[area.primerType].packs) };
        }
        if (m2Value > 0 && area.needsPrimer !== true) {
          return { ...area, needsPrimer: true };
        }
        return area;
      }));
    }
  }, [currentStep, primerAreas.length]);

  // Auto-initialize design coat when entering Step 4
  useEffect(() => {
    if (currentStep === 4 && primerAreas.length > 0) {
      // Read the freshest `designCoatSelections` from the setter's `prev`
      // argument so we don't need it in the dep array (which would re-fire
      // the effect on every selection change and clobber user edits).
      setDesignCoatSelections(prev => {
        const next = { ...prev };
        let changed = false;
        primerAreas.forEach(area => {
          const m2Value = parseInt(area.m2) || 0;
          if (m2Value > 0 && !next[area.id]) {
            next[area.id] = {
              productType: 'comfort',
              packs: calculateOptimalPacks(m2Value, DESIGN_COAT_PRODUCTS.comfort.packs),
              crystalUnits: Math.ceil(m2Value / DESIGN_COAT_PRODUCTS.crystal.coverageMin),
            };
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [currentStep, primerAreas]);

  // Auto-initialize top coat when entering Step 6
  useEffect(() => {
    if (currentStep === 6 && primerAreas.length > 0) {
      setPrimerAreas(prev => prev.map(area => {
        const m2Value = parseInt(area.m2) || 0;
        if (m2Value > 0 && !area.topCoatType) {
          const product = TOP_COAT_PRODUCTS.polyurethane;
          return {
            ...area,
            topCoatType: 'polyurethane',
            topCoatFinish: product.finishes[0],
            topCoatPacks: calculateOptimalPacks(m2Value, product.packs)
          };
        }
        return area;
      }));
    }
  }, [currentStep, primerAreas.length]);

  // Legacy primerArea functions
  const addPrimerArea = () => {
    setPrimerAreas(prev => [...prev, {
      id: generateAreaId(),
      name: `Area ${prev.length + 1}`,
      m2: '',
      needsSmoothingCompounds: null,
      smoothingCompounds: {},
      needsSubFloorPrimer: null,
      subFloorPrimers: {},
      needsPrimer: null,
      primerType: '1_part',
      color: 'White',
      packs: [],
      topCoatType: null,
      topCoatFinish: null,
      topCoatPacks: []
    }]);
  };

  const removePrimerArea = (areaId) => {
    setPrimerAreas(prev => prev.filter(a => a.id !== areaId));
  };

  const updatePrimerArea = (areaId, field, value) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const updated = { ...area, [field]: value };
      if (field === 'm2' || field === 'primerType') {
        const m2Value = field === 'm2' ? parseInt(value) || 0 : parseInt(area.m2) || 0;
        const type = field === 'primerType' ? value : area.primerType;
        if (m2Value > 0 && type) {
          updated.packs = calculateOptimalPacks(m2Value, EPOXY_PRIMERS[type].packs);
        } else { updated.packs = []; }
      }
      if (field === 'primerType') {
        const availableColors = EPOXY_PRIMERS[value].colors;
        if (!availableColors.includes(area.color)) { updated.color = availableColors[0]; }
      }
      return updated;
    }));
  };

  const updateAreaPackQty = (areaId, packSize, delta) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const existingPack = area.packs.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = area.packs.filter(p => p.size !== packSize); }
        else { newPacks = area.packs.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = EPOXY_PRIMERS[area.primerType].packs.find(p => p.size === packSize);
        newPacks = [...area.packs, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = area.packs; }
      return { ...area, packs: newPacks };
    }));
  };

  const updateAreaField = (areaId, field, value) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      return { ...area, [field]: value };
    }));
  };

  const updateAreaFloorPrepQty = (areaId, category, productId, delta) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const currentQty = area[category]?.[productId] || 0;
      return {
        ...area,
        [category]: { ...area[category], [productId]: Math.max(0, currentQty + delta) }
      };
    }));
  };

  const initAreaTopCoat = (areaId, productType) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      const m2Value = parseInt(area.m2) || 0;
      const product = TOP_COAT_PRODUCTS[productType];
      return {
        ...area,
        topCoatType: productType,
        topCoatFinish: product.finishes[0],
        topCoatPacks: calculateOptimalPacks(m2Value, product.packs)
      };
    }));
  };

  const updateAreaTopCoatFinish = (areaId, finish) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      return { ...area, topCoatFinish: finish };
    }));
  };

  const updateAreaTopCoatPackQty = (areaId, packSize, delta) => {
    setPrimerAreas(prev => prev.map(area => {
      if (area.id !== areaId) return area;
      if (!area.topCoatType) return area;
      const product = TOP_COAT_PRODUCTS[area.topCoatType];
      const existingPack = (area.topCoatPacks || []).find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = area.topCoatPacks.filter(p => p.size !== packSize); }
        else { newPacks = area.topCoatPacks.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = product.packs.find(p => p.size === packSize);
        newPacks = [...(area.topCoatPacks || []), { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = area.topCoatPacks || []; }
      return { ...area, topCoatPacks: newPacks };
    }));
  };

  const getAreaTopCoatSubtotal = (area) => {
    if (!area.topCoatPacks) return 0;
    return area.topCoatPacks.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  const getAreaCoverage = (area) => {
    return area.packs.reduce((sum, p) => sum + (p.size * p.quantity), 0);
  };

  const getAreaSubtotal = (area) => {
    return area.packs.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  // Legacy design coat functions
  const initializeDesignCoatForArea = (areaId, m2) => {
    const m2Value = parseInt(m2) || 0;
    if (m2Value <= 0) return;
    setDesignCoatSelections(prev => {
      if (prev[areaId]) return prev;
      return {
        ...prev,
        [areaId]: {
          productType: 'comfort',
          packs: calculateOptimalPacks(m2Value, DESIGN_COAT_PRODUCTS.comfort.packs),
          crystalUnits: Math.ceil(m2Value / DESIGN_COAT_PRODUCTS.crystal.coverageMin)
        }
      };
    });
  };

  const updateDesignCoatProduct = (areaId, productType, m2) => {
    const m2Value = parseInt(m2) || 0;
    setDesignCoatSelections(prev => ({
      ...prev,
      [areaId]: {
        ...prev[areaId],
        productType,
        packs: productType === 'comfort' ? calculateOptimalPacks(m2Value, DESIGN_COAT_PRODUCTS.comfort.packs) : [],
        crystalUnits: productType === 'crystal' ? Math.ceil(m2Value / DESIGN_COAT_PRODUCTS.crystal.coverageMin) : 0
      }
    }));
  };

  const updateDesignCoatPackQtyLegacy = (areaId, packSize, delta) => {
    setDesignCoatSelections(prev => {
      const current = prev[areaId];
      if (!current) return prev;
      const existingPack = current.packs.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = current.packs.filter(p => p.size !== packSize); }
        else { newPacks = current.packs.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = DESIGN_COAT_PRODUCTS.comfort.packs.find(p => p.size === packSize);
        newPacks = [...current.packs, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = current.packs; }
      return { ...prev, [areaId]: { ...current, packs: newPacks } };
    });
  };

  const updateCrystalUnitsLegacy = (areaId, delta) => {
    setDesignCoatSelections(prev => ({
      ...prev,
      [areaId]: {
        ...prev[areaId],
        crystalUnits: Math.max(0, (prev[areaId]?.crystalUnits || 0) + delta)
      }
    }));
  };

  const getDesignCoatSubtotal = (areaId) => {
    const selection = designCoatSelections[areaId];
    if (!selection) return 0;
    if (selection.productType === 'comfort') {
      return selection.packs.reduce((sum, p) => sum + (p.price * p.quantity), 0);
    } else {
      return selection.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.price;
    }
  };

  const getDesignCoatCoverage = (areaId) => {
    const selection = designCoatSelections[areaId];
    if (!selection) return 0;
    if (selection.productType === 'comfort') {
      return selection.packs.reduce((sum, p) => sum + (p.size * p.quantity), 0);
    } else {
      return `${selection.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.coverageMin}-${selection.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.coverageMax}`;
    }
  };

  // Legacy top coat functions
  const selectTopCoatProduct = (productType) => {
    const totalM2 = primerAreas.reduce((sum, a) => sum + (parseInt(a.m2) || 0), 0);
    const product = TOP_COAT_PRODUCTS[productType];
    const packs = calculateOptimalPacks(totalM2, product.packs);
    setTopCoatSelections({ productType, finish: product.finishes[0], packs });
  };

  const updateTopCoatFinishLegacy = (finish) => {
    setTopCoatSelections(prev => ({ ...prev, finish }));
  };

  const updateTopCoatPackQtyLegacy = (packSize, delta) => {
    setTopCoatSelections(prev => {
      const product = TOP_COAT_PRODUCTS[prev.productType];
      const existingPack = prev.packs.find(p => p.size === packSize);
      let newPacks;
      if (existingPack) {
        const newQty = Math.max(0, existingPack.quantity + delta);
        if (newQty === 0) { newPacks = prev.packs.filter(p => p.size !== packSize); }
        else { newPacks = prev.packs.map(p => p.size === packSize ? { ...p, quantity: newQty } : p); }
      } else if (delta > 0) {
        const packInfo = product.packs.find(p => p.size === packSize);
        newPacks = [...prev.packs, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
      } else { newPacks = prev.packs; }
      return { ...prev, packs: newPacks };
    });
  };

  const getTopCoatSubtotal = () => {
    return topCoatSelections.packs.reduce((sum, p) => sum + (p.price * p.quantity), 0);
  };

  const getTopCoatCoverage = () => {
    return topCoatSelections.packs.reduce((sum, p) => sum + (p.size * p.quantity), 0);
  };

  // Pigment functions
  const addMetallicPowder = (color, size) => {
    setPigmentSelections(prev => {
      const existing = prev.metallicPowders.find(p => p.color === color && p.size === size);
      if (existing) {
        return { ...prev, metallicPowders: prev.metallicPowders.map(p => p.color === color && p.size === size ? { ...p, quantity: p.quantity + 1 } : p) };
      }
      return { ...prev, metallicPowders: [...prev.metallicPowders, { color, size, quantity: 1 }] };
    });
  };

  const updateMetallicPowderQty = (color, size, delta) => {
    setPigmentSelections(prev => {
      const newPowders = prev.metallicPowders.map(p => {
        if (p.color === color && p.size === size) { return { ...p, quantity: Math.max(0, p.quantity + delta) }; }
        return p;
      }).filter(p => p.quantity > 0);
      return { ...prev, metallicPowders: newPowders };
    });
  };

  const removeMetallicPowder = (color) => {
    setPigmentSelections(prev => ({
      ...prev,
      metallicPowders: prev.metallicPowders.filter(p => p.color !== color),
    }));
  };

  const addPigmentPaste = (color) => {
    setPigmentSelections(prev => {
      const existing = prev.pigmentPaste.find(p => p.color === color);
      if (existing) return prev;
      const totalM2 = areas.reduce((sum, a) => sum + (parseInt(a.m2) || 0), 0);
      const packs = calculateOptimalPacks(totalM2, PIGMENTS_PRODUCTS.pigmentPaste.packs);
      return { ...prev, pigmentPaste: [...prev.pigmentPaste, { color, packs }] };
    });
  };

  const removePigmentPaste = (color) => {
    setPigmentSelections(prev => ({ ...prev, pigmentPaste: prev.pigmentPaste.filter(p => p.color !== color) }));
  };

  const updatePigmentPastePackQty = (color, packSize, delta) => {
    setPigmentSelections(prev => ({
      ...prev,
      pigmentPaste: prev.pigmentPaste.map(p => {
        if (p.color !== color) return p;
        const existingPack = p.packs.find(pk => pk.size === packSize);
        let newPacks;
        if (existingPack) {
          const newQty = Math.max(0, existingPack.quantity + delta);
          if (newQty === 0) { newPacks = p.packs.filter(pk => pk.size !== packSize); }
          else { newPacks = p.packs.map(pk => pk.size === packSize ? { ...pk, quantity: newQty } : pk); }
        } else if (delta > 0) {
          const packInfo = PIGMENTS_PRODUCTS.pigmentPaste.packs.find(pk => pk.size === packSize);
          newPacks = [...p.packs, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
        } else { newPacks = p.packs; }
        return { ...p, packs: newPacks };
      })
    }));
  };

  const addGlitter = (type) => {
    setPigmentSelections(prev => {
      const existing = prev.glitter.find(g => g.type === type);
      if (existing) return prev;
      const totalM2 = areas.reduce((sum, a) => sum + (parseInt(a.m2) || 0), 0);
      const packs = calculateOptimalPacks(totalM2, PIGMENTS_PRODUCTS.glitter.packs);
      return { ...prev, glitter: [...prev.glitter, { type, packs }] };
    });
  };

  const removeGlitter = (type) => {
    setPigmentSelections(prev => ({ ...prev, glitter: prev.glitter.filter(g => g.type !== type) }));
  };

  const updateGlitterPackQty = (type, packSize, delta) => {
    setPigmentSelections(prev => ({
      ...prev,
      glitter: prev.glitter.map(g => {
        if (g.type !== type) return g;
        const existingPack = g.packs.find(pk => pk.size === packSize);
        let newPacks;
        if (existingPack) {
          const newQty = Math.max(0, existingPack.quantity + delta);
          if (newQty === 0) { newPacks = g.packs.filter(pk => pk.size !== packSize); }
          else { newPacks = g.packs.map(pk => pk.size === packSize ? { ...pk, quantity: newQty } : pk); }
        } else if (delta > 0) {
          const packInfo = PIGMENTS_PRODUCTS.glitter.packs.find(pk => pk.size === packSize);
          newPacks = [...g.packs, { ...packInfo, quantity: 1 }].sort((a, b) => b.size - a.size);
        } else { newPacks = g.packs; }
        return { ...g, packs: newPacks };
      })
    }));
  };

  const addSpray = (sprayType, color) => {
    const key = sprayType === 'marble' ? 'marbleSprays' : 'metallicSprays';
    setPigmentSelections(prev => {
      const existing = prev[key].find(s => s.color === color);
      if (existing) { return { ...prev, [key]: prev[key].map(s => s.color === color ? { ...s, quantity: s.quantity + 1 } : s) }; }
      return { ...prev, [key]: [...prev[key], { color, quantity: 1 }] };
    });
  };

  const updateSprayQty = (sprayType, color, delta) => {
    const key = sprayType === 'marble' ? 'marbleSprays' : 'metallicSprays';
    setPigmentSelections(prev => ({
      ...prev,
      [key]: prev[key].map(s => {
        if (s.color === color) { return { ...s, quantity: Math.max(0, s.quantity + delta) }; }
        return s;
      }).filter(s => s.quantity > 0)
    }));
  };

  const getPigmentsSubtotal = () => {
    let total = 0;
    pigmentSelections.metallicPowders.forEach(p => {
      const sizeInfo = PIGMENTS_PRODUCTS.metallicPowder.sizes.find(s => s.size === p.size);
      total += (sizeInfo?.price || 0) * p.quantity;
    });
    pigmentSelections.pigmentPaste.forEach(p => { p.packs.forEach(pk => { total += pk.price * pk.quantity; }); });
    pigmentSelections.glitter.forEach(g => { g.packs.forEach(pk => { total += pk.price * pk.quantity; }); });
    pigmentSelections.marbleSprays.forEach(s => { total += PIGMENTS_PRODUCTS.sprays.marbleSpray.price * s.quantity; });
    pigmentSelections.metallicSprays.forEach(s => { total += PIGMENTS_PRODUCTS.sprays.metallicSpray.price * s.quantity; });
    return total;
  };

  // Quantities for floor prep (legacy)
  const [quantities, setQuantities] = useState({ smoothing_compounds: {}, primers: {} });

  const updateQuantity = (category, productId, delta) => {
    setQuantities(prev => ({
      ...prev,
      [category]: { ...prev[category], [productId]: Math.max(0, (prev[category][productId] || 0) + delta) }
    }));
  };

  const setQuantity = (category, productId, value) => {
    const numValue = parseInt(value) || 0;
    setQuantities(prev => ({
      ...prev,
      [category]: { ...prev[category], [productId]: Math.max(0, numValue) }
    }));
  };

  // Calculate totals
  const calculateTotal = () => {
    let total = 0;
    primerAreas.forEach(area => {
      if (area.needsSmoothingCompounds) {
        FLOOR_PREP_PRODUCTS.smoothing_compounds.forEach(product => {
          const qty = area.smoothingCompounds?.[product.id] || 0;
          total += qty * product.price;
        });
      }
      if (area.needsSubFloorPrimer) {
        FLOOR_PREP_PRODUCTS.primers.forEach(product => {
          const qty = area.subFloorPrimers?.[product.id] || 0;
          total += qty * product.price;
        });
      }
      area.packs.forEach(p => { total += p.price * p.quantity; });
      const designCoat = designCoatSelections[area.id];
      if (designCoat) {
        if (designCoat.productType === 'comfort') {
          designCoat.packs.forEach(p => { total += p.price * p.quantity; });
        } else {
          total += designCoat.crystalUnits * DESIGN_COAT_PRODUCTS.crystal.price;
        }
      }
      if (area.topCoatPacks) { area.topCoatPacks.forEach(p => { total += p.price * p.quantity; }); }
    });
    // Pigments & effects (global)
    total += getPigmentsSubtotal();
    return total;
  };

  const getItemCount = () => {
    let count = 0;
    primerAreas.forEach(area => {
      if (area.needsSmoothingCompounds) { Object.values(area.smoothingCompounds || {}).forEach(qty => count += qty); }
      if (area.needsSubFloorPrimer) { Object.values(area.subFloorPrimers || {}).forEach(qty => count += qty); }
      area.packs.forEach(p => count += p.quantity);
      const designCoat = designCoatSelections[area.id];
      if (designCoat) {
        if (designCoat.productType === 'comfort') { designCoat.packs.forEach(p => count += p.quantity); }
        else { count += designCoat.crystalUnits; }
      }
      if (area.topCoatPacks) { area.topCoatPacks.forEach(p => count += p.quantity); }
    });
    pigmentSelections.metallicPowders.forEach(p => count += p.quantity);
    pigmentSelections.pigmentPaste.forEach(p => p.packs.forEach(pk => count += pk.quantity));
    pigmentSelections.glitter.forEach(g => g.packs.forEach(pk => count += pk.quantity));
    pigmentSelections.marbleSprays.forEach(s => count += s.quantity);
    pigmentSelections.metallicSprays.forEach(s => count += s.quantity);
    return count;
  };

  const steps = [
    { num: 1, title: 'Define Areas' },
    { num: 2, title: 'Floor Prep' },
    { num: 3, title: 'Primer' },
    { num: 4, title: 'Design Coat' },
    { num: 5, title: 'Pigments' },
    { num: 6, title: 'Top Coat' },
    { num: 7, title: 'Summary' }
  ];

  return {
    // State
    primerAreas, setPrimerAreas, areas, designCoatSelections, pigmentSelections, topCoatSelections,
    needsSmoothingCompounds, setNeedsSmoothingCompounds,
    needsPrimer, setNeedsPrimer,
    quantities,
    steps,
    // New area functions
    addArea, removeArea, updateArea, updateAreaWithPrimerCalc,
    initializePrimerPacks, updateAreaPrimerPackQty,
    initializeDesignCoat, updateDesignCoatType, updateDesignCoatPackQty, updateCrystalUnits,
    initializeTopCoat, updateTopCoatFinish, updateTopCoatPackQty,
    // Legacy primerArea functions
    addPrimerArea, removePrimerArea, updatePrimerArea, updateAreaPackQty,
    updateAreaField, updateAreaFloorPrepQty,
    initAreaTopCoat, updateAreaTopCoatFinish, updateAreaTopCoatPackQty,
    getAreaTopCoatSubtotal, getAreaCoverage, getAreaSubtotal,
    // Legacy design coat
    initializeDesignCoatForArea, updateDesignCoatProduct,
    updateDesignCoatPackQtyLegacy, updateCrystalUnitsLegacy,
    getDesignCoatSubtotal, getDesignCoatCoverage,
    // Legacy top coat
    selectTopCoatProduct, updateTopCoatFinishLegacy, updateTopCoatPackQtyLegacy,
    getTopCoatSubtotal, getTopCoatCoverage,
    // Pigments
    addMetallicPowder, updateMetallicPowderQty, removeMetallicPowder,
    addPigmentPaste, removePigmentPaste, updatePigmentPastePackQty,
    addGlitter, removeGlitter, updateGlitterPackQty,
    addSpray, updateSprayQty, getPigmentsSubtotal,
    // Quantities
    updateQuantity, setQuantity,
    // Totals
    calculateTotal, getItemCount
  };
};

export default useMetallicOrder;
